import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useDiagramSettings } from '../../../hooks/useDiagramSettings';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import { IncrementalOrgLayoutCommand } from '../commands/IncrementalOrgLayoutCommand';
import { LayoutOrgChartCommand } from '../commands/LayoutOrgChartCommand';
import {
  isOrgNode,
  isVisibleOrgLayoutConnector,
  buildHierarchyMaps,
  calculateSubtreeWidths,
  recenterChildren,
  type HierarchyMaps,
  ORG_DISPLAY_RULE_LABEL_CLEARANCE,
} from '../services/graphAnalysis';
import { applyOrgChartCollapse } from '../services/orgChartCollapseProjection';
import type { Point2D } from '../types/layout.types';
import { isOrgEdge } from '../types/layout.types';
import type { Edge, Node } from '../../../core/models/scene';
import { computeOrgCollapseLayoutTransition } from '../services/OrgAutoLayoutLifecycle';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';

const DEBOUNCE_MS = 100;
const MAX_WAIT_MS = 500;

/**
 * Delay (ms) after isInitialLoading→false before arming auto-layout.
 * This gives Yjs time to finish syncing any remaining data so we don't
 * mistake late-arriving edges for user-initiated structural changes.
 */
const ARM_DELAY_MS = 600;

/**
 * Additional delay (ms) after arm to re-check node dimensions.
 * SSE may deliver updated heights (e.g. h=72 → h=94) after arming;
 * this secondary check re-runs layout if dimensions changed.
 */
const DIM_RECHECK_DELAY_MS = 600;

const ORG_LAYOUT_EDGE_TYPES = new Set(['reporting', 'dotted', 'matrix', 'team', 'occupies']);

const getOrgLayoutEdges = (edgeList: Edge[]): Edge[] =>
  edgeList.filter(
    (edge) =>
      ORG_LAYOUT_EDGE_TYPES.has(edge.type as unknown as string) &&
      isVisibleOrgLayoutConnector(edge),
  );

const normalizeProjectedOrgEdgeAnchors = (layoutNodes: Node[], layoutEdges: Edge[]): void => {
  const orgEdges = getOrgLayoutEdges(layoutEdges);
  if (orgEdges.length === 0) return;

  getCommandManager().execute(
    new LayoutOrgChartCommand({
      positions: new Map(),
      edges: orgEdges,
      nodes: layoutNodes,
      forceAnchorNormalization: true,
      skipHistory: true,
      label: 'Normalize org edge anchors',
    }),
  );
};

/** Snapshot org node dimensions (width + height) for change detection. */
function snapshotDimensions(): Map<string, number> {
  const dims = new Map<string, number>();
  const { nodes } = useDiagramStore.getState();
  for (const n of nodes) {
    if (isOrgNode(n)) {
      // Pack w+h into a single number for cheap comparison
      const width = resolveEffectiveShapeDimension(n, 'width') ?? 0;
      const height = resolveEffectiveShapeDimension(n, 'height') ?? 0;
      dims.set(n.id, (width * 100000) + height);
    }
  }
  return dims;
}

/** Check if any node dimensions changed since the snapshot. */
function dimensionsChanged(snapshot: Map<string, number>): boolean {
  const { nodes } = useDiagramStore.getState();
  for (const n of nodes) {
    if (isOrgNode(n)) {
      const width = resolveEffectiveShapeDimension(n, 'width') ?? 0;
      const height = resolveEffectiveShapeDimension(n, 'height') ?? 0;
      const packed = (width * 100000) + height;
      if (snapshot.get(n.id) !== packed) return true;
    }
  }
  return false;
}

/**
 * Hook that provides incremental auto-layout for org charts.
 *
 * Uses command-driven change detection: commands call markDirty() when they modify
 * the org chart structure. This avoids O(n) position polling.
 *
 * Layout changes are applied via IncrementalOrgLayoutCommand to support undo/redo.
 *
 * @returns Object with markDirty function and enabled state
 */
export function useAutoLayout() {
  const { doc } = useDiagramDocument();
  const { read: readSettings } = useDiagramSettings(doc);
  const autoLayoutEnabled = useOrgChartViewStore(s => s.autoLayoutEnabled);
  const collapsedNodeIds = useOrgChartViewStore(s => s.collapsedNodeIds);
  const hasActiveDisplayRuleLabels = useViewRulesStore((state) =>
    state.rules.some((rule) => rule.enabled && !!rule.display.label),
  );

  // Refs for mutable state that shouldn't trigger re-renders
  const dirtyNodesRef = useRef<Set<string>>(new Set());
  const widthCacheRef = useRef<Map<string, number>>(new Map());
  const hierarchyRef = useRef<HierarchyMaps | null>(null);
  const canceledRef = useRef(false);

  // Suppresses layout during initial Yjs data load.
  // Starts suppressed; arms after isInitialLoading transitions false + delay.
  const armedRef = useRef(false);

  // Ref to hold applyLayout function (needed for markDirty to call it)
  const applyLayoutRef = useRef<(() => void) | null>(null);

  /**
   * Marks a node as needing layout recalculation.
   * Also marks all ancestors as dirty since their centering may need adjustment.
   * Triggers the debounced layout application.
   */
  const markDirty = useCallback((nodeId: string) => {
    // Always add the changed node
    dirtyNodesRef.current.add(nodeId);

    // Walk up to root, marking ancestors dirty
    if (hierarchyRef.current) {
      let currentId = hierarchyRef.current.parentByChild.get(nodeId);
      while (currentId) {
        dirtyNodesRef.current.add(currentId);
        currentId = hierarchyRef.current.parentByChild.get(currentId);
      }
    }

    // Trigger layout (debounced)
    if (applyLayoutRef.current) {
      applyLayoutRef.current();
    }
  }, []);

  /**
   * Processes dirty nodes and applies incremental layout.
   * Uses getState() to read fresh state at execution time (avoids stale closures).
   */
  const applyLayout = useDebouncedCallback(
    () => {
      if (canceledRef.current) return;

      const viewState = useOrgChartViewStore.getState();
      const enabled = viewState.autoLayoutEnabled;
      if (!enabled) {
        dirtyNodesRef.current.clear();
        return;
      }

      if (!armedRef.current) {
        return;
      }

      const { nodes, edges } = useDiagramStore.getState();
      const { collapsedNodeIds: collapsedIds, asOfDate: viewAsOfDate } = useOrgChartViewStore.getState();
      if (viewAsOfDate) {
        dirtyNodesRef.current.clear();
        return;
      }
      const collapseProjection = applyOrgChartCollapse(nodes, edges, collapsedIds);
      const layoutNodes = collapseProjection.nodes;
      const layoutEdges = collapseProjection.edges;

      const dirty = dirtyNodesRef.current;
      if (dirty.size === 0) {
        return;
      }

      // Build current positions map first (needed for stable child ordering)
      const currentPositions = new Map<string, Point2D>();
      const orgNodeIds = new Set<string>();
      const nodeById = new Map<string, Node>();

      layoutNodes.forEach(node => {
        if (isOrgNode(node)) {
          orgNodeIds.add(node.id);
          nodeById.set(node.id, node);
          currentPositions.set(node.id, { x: node.x, y: node.y });
        }
      });

      if (orgNodeIds.size === 0) {
        dirtyNodesRef.current.clear();
        return;
      }

      // Build hierarchy with positions to preserve visual order
      const hierarchy = buildHierarchyMaps(orgNodeIds, layoutEdges, currentPositions);
      hierarchyRef.current = hierarchy;

      const settings = readSettings();
      const nodeSpacing = settings.orgchart?.nodeSpacing;
      const verticalSpacing = settings.orgchart?.verticalSpacing;
      const layoutOpts = {
        nodeSpacing,
        verticalSpacing,
        displayRuleLabelClearance: hasActiveDisplayRuleLabels ? ORG_DISPLAY_RULE_LABEL_CLEARANCE : 0,
      };

      // Calculate subtree widths (bottom-up) with offsets for asymmetric subtrees
      const subtreeOffsets = new Map<string, number>();
      const widths = calculateSubtreeWidths(hierarchy, nodeById, nodeSpacing, subtreeOffsets, layoutOpts);
      widthCacheRef.current = widths;

      // Find the highest affected ancestor across all dirty nodes
      // For each dirty node, we need to go to its parent so the parent can recenter it with siblings
      let highestAffected: string | null = null;
      let minDepth = Infinity;

      dirty.forEach(nodeId => {
        if (!orgNodeIds.has(nodeId)) return;

        // Go to the parent of the dirty node - that's who needs to recenter
        // If no parent (it's a root), use the node itself
        let affected = hierarchy.parentByChild.get(nodeId) ?? nodeId;

        // Keep going up if the parent also has a parent and this might affect layout
        // (Simple approach: go to grandparent to ensure proper recentering)
        const grandparent = hierarchy.parentByChild.get(affected);
        if (grandparent) {
          affected = grandparent;
        }

        // Calculate depth to find the highest (closest to root) - lower depth = closer to root
        let depth = 0;
        let current: string | undefined = affected;
        while (current) {
          depth++;
          current = hierarchy.parentByChild.get(current);
        }

        // Select the node with minimum depth (closest to root)
        if (depth < minDepth) {
          minDepth = depth;
          highestAffected = affected;
        }
      });

      if (!highestAffected) {
        dirtyNodesRef.current.clear();
        return;
      }

      // Get position of highest affected ancestor
      const startPos = currentPositions.get(highestAffected);
      if (!startPos) {
        dirtyNodesRef.current.clear();
        return;
      }

      // Calculate new positions (top-down from affected ancestor)
      const newPositions = recenterChildren(
        highestAffected,
        startPos,
        hierarchy,
        nodeById,
        widths,
        currentPositions,
        nodeSpacing,
        subtreeOffsets,
        layoutOpts,
      );

      // Apply via command for undo/redo support
      if (newPositions.size > 0) {
        const command = new IncrementalOrgLayoutCommand({
          positions: newPositions,
          affectedNodeIds: Array.from(newPositions.keys()),
          label: 'Auto-layout adjustment',
        });

        const commandManager = getCommandManager();
        commandManager.execute(command);
      }

      normalizeProjectedOrgEdgeAnchors(layoutNodes, layoutEdges);

      // Clear dirty set after processing
      dirtyNodesRef.current.clear();
    },
    DEBOUNCE_MS,
    { maxWait: MAX_WAIT_MS }
  );

  // Store applyLayout in ref so markDirty can call it
  applyLayoutRef.current = applyLayout;

  // Track previous collapsed IDs to detect expand (removal from list)
  const prevCollapsedRef = useRef<string[]>(collapsedNodeIds);

  // Rebuild hierarchy when collapse state changes.
  // When a node is expanded (removed from collapsed list), mark it dirty
  // so its children get properly laid out.
  useEffect(() => {
    const prevCollapsed = prevCollapsedRef.current;
    prevCollapsedRef.current = collapsedNodeIds;

    // Skip all layout work when auto-layout is disabled
    if (!autoLayoutEnabled) return;
    if (useOrgChartViewStore.getState().asOfDate) return;

    const {
      expandedNodeIds: expandedNodes,
      collapsedNodeIds: newlyCollapsed,
    } = computeOrgCollapseLayoutTransition(
      prevCollapsed,
      collapsedNodeIds,
      armedRef.current,
    );

    const frameId = requestAnimationFrame(() => {
      const { nodes, edges } = useDiagramStore.getState();
      const collapseProjection = applyOrgChartCollapse(nodes, edges, collapsedNodeIds);
      const layoutNodes = collapseProjection.nodes;
      const layoutEdges = collapseProjection.edges;

      const orgNodeIds = new Set<string>();
      const currentPositions = new Map<string, Point2D>();
      layoutNodes.forEach(node => {
        if (isOrgNode(node)) {
          orgNodeIds.add(node.id);
          currentPositions.set(node.id, { x: node.x, y: node.y });
        }
      });

      // For newly collapsed nodes, use OLD hierarchy to find parent before rebuilding
      if (newlyCollapsed.length > 0 && hierarchyRef.current) {
        const oldHierarchy = hierarchyRef.current;
        newlyCollapsed.forEach(id => {
          const parentId = oldHierarchy.parentByChild.get(id);
          if (parentId && orgNodeIds.has(parentId)) {
            dirtyNodesRef.current.add(parentId);
          }
          // Also mark the collapsed node itself dirty
          if (orgNodeIds.has(id)) {
            dirtyNodesRef.current.add(id);
          }
        });
      }

      hierarchyRef.current = buildHierarchyMaps(orgNodeIds, layoutEdges, currentPositions);
      widthCacheRef.current.clear();

      // Mark expanded nodes dirty so their children get re-laid out
      if (expandedNodes.length > 0) {
        expandedNodes.forEach(id => {
          if (orgNodeIds.has(id)) {
            dirtyNodesRef.current.add(id);
          }
        });
      }

      // Trigger layout if expanded OR collapsed
      if ((expandedNodes.length > 0 || newlyCollapsed.length > 0) && dirtyNodesRef.current.size > 0) {
        applyLayout();
      }
    });
    return () => cancelAnimationFrame(frameId);
  }, [collapsedNodeIds, applyLayout, autoLayoutEnabled]);

  useEffect(() => {
    if (!autoLayoutEnabled || !armedRef.current) return;
    const { nodes, edges } = useDiagramStore.getState();
    const collapseProjection = applyOrgChartCollapse(nodes, edges, collapsedNodeIds);
    const orgRootIds = collapseProjection.nodes
      .filter((node) => isOrgNode(node))
      .map((node) => node.id);

    if (orgRootIds.length === 0) return;

    orgRootIds.forEach((id) => dirtyNodesRef.current.add(id));
    applyLayout();
  }, [applyLayout, autoLayoutEnabled, collapsedNodeIds, hasActiveDisplayRuleLabels]);

  // Subscribe to structure changes (nodes and edges) to detect additions and deletions
  useEffect(() => {
    const unsubscribe = useDiagramStore.subscribe(
      (state, prevState) => {
        // Skip all work when auto-layout is disabled
        const { autoLayoutEnabled: enabled, asOfDate: viewAsOfDate } = useOrgChartViewStore.getState();
        if (!enabled) return;
        if (viewAsOfDate) return;

        const nodesChanged = state.nodes !== prevState.nodes;
        const edgesChanged = state.edges !== prevState.edges;

        if (!nodesChanged && !edgesChanged) return;

        // Fast exit: if only node positions changed (same count, edges unchanged),
        // skip the expensive collapse projection + hierarchy rebuild.
        // Structural changes (additions/deletions) always change array length.
        if (nodesChanged && !edgesChanged && state.nodes.length === prevState.nodes.length) {
          return;
        }

        const collapseProjection = applyOrgChartCollapse(state.nodes, state.edges, collapsedNodeIds);
        const layoutNodes = collapseProjection.nodes;
        const layoutEdges = collapseProjection.edges;

        // Build current org node set (visible only)
        const orgNodeIds = new Set<string>();
        layoutNodes.forEach(node => {
          if (isOrgNode(node)) {
            orgNodeIds.add(node.id);
          }
        });

        // Only mark nodes dirty after the initial load has settled (armedRef)
        if (armedRef.current) {
          // Detect added and deleted reporting edges and mark parent nodes dirty
          if (edgesChanged) {
            const currEdgeIds = new Set(state.edges.map(e => e.id));
            const prevEdgeIds = new Set(prevState.edges.map(e => e.id));

            // Deleted edges — mark old parent dirty for recentering
            prevState.edges.forEach(edge => {
              if (
                !currEdgeIds.has(edge.id) &&
                isOrgEdge(edge) &&
                isVisibleOrgLayoutConnector(edge)
              ) {
                if (orgNodeIds.has(edge.to)) {
                  dirtyNodesRef.current.add(edge.to);
                }
              }
            });

            // New edges — mark new parent dirty so children get positioned
            state.edges.forEach(edge => {
              if (
                !prevEdgeIds.has(edge.id) &&
                isOrgEdge(edge) &&
                isVisibleOrgLayoutConnector(edge)
              ) {
                if (orgNodeIds.has(edge.to)) {
                  dirtyNodesRef.current.add(edge.to);
                }
              }
            });
          }

          // Detect deleted org nodes - use previous hierarchy to find their parents
          if (nodesChanged && hierarchyRef.current) {
            const currNodeIds = new Set(state.nodes.map(n => n.id));
            const prevHierarchy = hierarchyRef.current;

            prevState.nodes.forEach(node => {
              if (isOrgNode(node) && !currNodeIds.has(node.id)) {
                // Node was deleted - find its parent from previous hierarchy
                const parentId = prevHierarchy.parentByChild.get(node.id);
                if (parentId && orgNodeIds.has(parentId)) {
                  dirtyNodesRef.current.add(parentId);
                }
              }
            });
          }
        }

        // Build positions for stable ordering
        const currentPositions = new Map<string, Point2D>();
        layoutNodes.forEach(node => {
          if (isOrgNode(node)) {
            currentPositions.set(node.id, { x: node.x, y: node.y });
          }
        });

        // Rebuild hierarchy with current state and positions
        hierarchyRef.current = buildHierarchyMaps(orgNodeIds, layoutEdges, currentPositions);
        widthCacheRef.current.clear(); // Invalidate width cache on structure change

        if (armedRef.current) {
          // Detect new org nodes — mark their parent dirty using the rebuilt hierarchy
          if (nodesChanged) {
            const prevNodeIds = new Set(prevState.nodes.map(n => n.id));
            const newHierarchy = hierarchyRef.current;

            state.nodes.forEach(node => {
              if (isOrgNode(node) && !prevNodeIds.has(node.id)) {
                const parentId = newHierarchy.parentByChild.get(node.id);
                if (parentId && orgNodeIds.has(parentId)) {
                  dirtyNodesRef.current.add(parentId);
                }
              }
            });
          }

          // Trigger layout if we have dirty nodes
          if (dirtyNodesRef.current.size > 0) {
            applyLayout();
          }
        }
      }
    );

    return unsubscribe;
  }, [applyLayout, collapsedNodeIds]);

  const flushLayoutAndClearDirty = useCallback(() => {
    applyLayout.flush();
    dirtyNodesRef.current.clear();
  }, [applyLayout]);

  // Register global markDirty, flush, and applyLayout when mounted, cleanup on unmount
  useEffect(() => {
    canceledRef.current = false;
    setGlobalMarkDirty(markDirty);
    setGlobalFlushLayoutAndClearDirty(flushLayoutAndClearDirty);
    setGlobalApplyLayout(applyLayout as DebouncedFn);

    // Capture ref values for cleanup (per react-hooks/exhaustive-deps)
    const dirtyNodes = dirtyNodesRef.current;

    return () => {
      canceledRef.current = true;
      applyLayout.cancel();
      dirtyNodes.clear();
      setGlobalMarkDirty(null);
      setGlobalFlushLayoutAndClearDirty(null);
      setGlobalApplyLayout(null);
    };
  }, [applyLayout, markDirty, flushLayoutAndClearDirty]);

  useEffect(() => {
    if (armedRef.current) return; // already armed

    // If already past initial loading (e.g. hot reload), arm immediately
    if (!useDiagramStore.getState().isInitialLoading) {
      let recheckTimer: ReturnType<typeof setTimeout>;
      const timer = setTimeout(() => {
        armedRef.current = true;
        const dimSnap = snapshotDimensions();
        const hadPendingLayout = dirtyNodesRef.current.size > 0;
        if (hadPendingLayout) applyLayout();
        // Secondary check: re-layout if SSE delivered dimension changes after arm
        recheckTimer = setTimeout(() => {
          if (hadPendingLayout && dimensionsChanged(dimSnap) && hierarchyRef.current) {
            hierarchyRef.current.rootIds.forEach(id => dirtyNodesRef.current.add(id));
            applyLayout();
          }
        }, DIM_RECHECK_DELAY_MS);
      }, ARM_DELAY_MS);
      return () => { clearTimeout(timer); clearTimeout(recheckTimer); };
    }

    // Wait for the transition
    const unsubscribe = useDiagramStore.subscribe(
      (state, prevState) => {
        if (prevState.isInitialLoading && !state.isInitialLoading) {
          setTimeout(() => {
            armedRef.current = true;
            // Process genuine structural changes that arrived while initial loading settled.
            const dimSnap = snapshotDimensions();
            const hadPendingLayout = dirtyNodesRef.current.size > 0;
            if (hadPendingLayout) {
              applyLayout();
            }
            // Secondary check: re-layout if SSE delivered dimension changes after arm
            setTimeout(() => {
              if (hadPendingLayout && dimensionsChanged(dimSnap) && hierarchyRef.current) {
                hierarchyRef.current.rootIds.forEach(id => dirtyNodesRef.current.add(id));
                applyLayout();
              }
            }, DIM_RECHECK_DELAY_MS);
          }, ARM_DELAY_MS);
          unsubscribe();
        }
      }
    );

    return unsubscribe;
  }, []);

  // Trigger layout when enabled and dirty nodes exist; clear stale dirty nodes when disabled
  useEffect(() => {
    if (autoLayoutEnabled) {
      if (dirtyNodesRef.current.size > 0) {
        applyLayout();
      }
    } else {
      dirtyNodesRef.current.clear();
      applyLayout.cancel();
    }
  }, [autoLayoutEnabled, applyLayout]);

  return {
    markDirty,
    enabled: autoLayoutEnabled,
    applyLayout, // Expose for manual triggering if needed
  };
}

/**
 * Global singleton for marking nodes dirty from commands.
 * Commands can import and call this without needing hook context.
 */
let globalMarkDirty: ((nodeId: string) => void) | null = null;

export function setGlobalMarkDirty(fn: ((nodeId: string) => void) | null): void {
  globalMarkDirty = fn;
}

export function getGlobalMarkDirty(): ((nodeId: string) => void) | null {
  return globalMarkDirty;
}

let globalFlushLayoutAndClearDirty: (() => void) | null = null;

export function setGlobalFlushLayoutAndClearDirty(fn: (() => void) | null): void {
  globalFlushLayoutAndClearDirty = fn;
}

export function getGlobalFlushLayoutAndClearDirty(): (() => void) | null {
  return globalFlushLayoutAndClearDirty;
}

type DebouncedFn = (() => void) & { flush: () => void; cancel: () => void };
let globalApplyLayout: DebouncedFn | null = null;

export function setGlobalApplyLayout(fn: DebouncedFn | null): void {
  globalApplyLayout = fn;
}

export function getGlobalApplyLayout(): DebouncedFn | null {
  return globalApplyLayout;
}

/**
 * Marks a node as needing layout recalculation.
 * Safe to call from commands - no-op if auto-layout is not mounted.
 */
export function markNodeDirty(nodeId: string): void {
  if (globalMarkDirty) {
    globalMarkDirty(nodeId);
  }
}
